import type { AgentUseCase, UseCaseContent } from "./types";

const en: UseCaseContent = {
  industry: "Retail & F&B",
  title: "Order processing agent",
  cardSummary:
    "Take customer orders on WhatsApp — capture items, confirm details, and push them straight into your system.",
  heroSubhead:
    "We design, build and deploy a WhatsApp AI agent that takes customer orders end to end — captures the items, confirms options and the total, collects delivery or pickup details, and drops a clean order into your system — so your team stops re-keying and stops missing messages.",
  snapshot: {
    valueLabel: "Very high",
    valueWhy:
      "WhatsApp is where most F&B & retail SMEs in the region already take orders — this captures revenue you lose after hours and at peak.",
    complexityLabel: "Low–medium",
    complexityWhy:
      "One linear prompt-chain on a single model; the real work is wiring WhatsApp, your menu/stock and your POS — no vision or heavy compliance.",
    timeToLive: "~2–4 weeks (one outlet)",
    bestFit: "F&B & retail SMEs taking orders on WhatsApp",
  },
  outcomes: [
    "Capture after-hours & peak demand you're losing to competitors today",
    "No more manual re-keying from chat into your system",
    "Fewer wrong orders — less waste, fewer refunds and remakes",
    "Staff back on the floor and the kitchen, not stuck on the phone",
  ],
  impact: {
    manualLabel: "Handling an order by hand",
    manualValue: "~4–6 min",
    agentLabel: "With the agent",
    agentValue: "~30 sec review",
    saved:
      "≈ 4–5 minutes saved per order — about 35–65 hours a month at 500–800 orders. And every after-hours order it catches is revenue you'd otherwise lose by morning: at ~RM 0.20 in AI cost, one saved order a day more than pays for it.",
    note: "Indicative; actual time and volume depend on order complexity and how much your menu varies.",
  },
  businessProblem:
    "An F&B or retail business in the region takes a stream of orders on WhatsApp. Staff read each message, work out exactly what the customer wants, clarify sizes and add-ons, tally the total, then re-key it into the POS or a spreadsheet — and confirm back to the customer. It's slow at lunch rush, error-prone, and after-hours messages sit unanswered until morning, by which point the customer has ordered elsewhere.",
  manualWorkflow: {
    steps: [
      "Read the incoming WhatsApp message",
      "Work out the items, sizes and add-ons",
      "Message back to clarify anything unclear",
      "Tally quantities and the total",
      "Re-key the order into the POS or a spreadsheet",
      "Confirm back to the customer",
    ],
    breaks:
      "The bottleneck is turning a free-text chat into a clean, correct order — the slow, repetitive part that clogs at peak and stops entirely after hours.",
  },
  agenticWorkflow: {
    narrative:
      "We build it as a prompt-chaining workflow: each customer message runs through a short, reliable chain — understand → match to your menu → confirm → capture details → create the order — with the agent re-asking only when something is genuinely ambiguous or out of stock.",
    steps: [
      "Understand the order from the chat",
      "Match items to your menu / catalogue",
      "Confirm items, options & the total",
      "Capture delivery or pickup details",
      "Create the order in your system",
    ],
    inLabel: "A customer messages on WhatsApp",
    outLabel: "A clean order in your system",
    loopAround: {
      index: 1,
      label: "Re-asks the customer only when an item is ambiguous or out of stock — otherwise it keeps moving.",
    },
    humanStep: "Your team gets a clean order to fulfil",
  },
  workedExample: {
    inLabel: "Customer message",
    inMessage:
      "nak order 2 nasi lemak ayam, 1 jgn pedas, teh ais kurang manis 1, pickup around 1pm",
    outLabel: "Clean order created",
    outLines: [
      "2× Nasi Lemak Ayam — 1 no sambal",
      "1× Teh Ais — less sweet",
      "Pickup · 1:00 PM today",
    ],
    outResult: "Total: RM 23.50 — confirmed",
    note: "Captured from one message, no staff typing · ~RM 0.10–0.30 in AI cost · written straight to your POS.",
  },
  workflowDetail: [
    {
      title: "Understand the order",
      detail:
        "The agent reads the customer's message — in Malay, English, Chinese or the mix people actually type — and works out what they're trying to order, including quantities and any special requests.",
    },
    {
      title: "Match to your menu",
      detail:
        "It maps what the customer said to your actual menu or catalogue, resolving nicknames and shorthand, and spotting anything that's out of stock or needs a size/option chosen.",
    },
    {
      title: "Confirm items, options and total",
      detail:
        "It confirms the itemised order and the total back to the customer in the chat, and only asks a question when something is genuinely unclear — not for every line.",
    },
    {
      title: "Capture delivery or pickup details",
      detail:
        "It collects the details you need — pickup or delivery, address, time, and (if you want) sends a payment link — without your staff typing anything.",
    },
    {
      title: "Create the order in your system",
      detail:
        "It writes a structured, itemised order into your POS, order sheet or CRM, ready for the kitchen or floor — no re-keying.",
    },
    {
      title: "Hand to your team",
      detail:
        "Your team gets a clean order to fulfil, and anything unusual is flagged for a human to glance at.",
    },
  ],
  howWeBuild:
    "Because it works from your live menu and stock, the agent only offers what's actually available — and because it writes orders through connected tools instead of free-typing them, nothing gets fat-fingered. It never charges a card on its own, and every order it creates is logged for you to audit.",
  agentLoop: [
    "the chat + your live menu & stock",
    "match the catalogue & write the order (MCP tools)",
    "re-check every item and the total",
  ],
  integrations: [
    "WhatsApp Business API",
    "Your menu or product catalogue (+ stock)",
    "Your POS, order sheet or CRM",
    "Optional: a payment link",
  ],
  architecture: [
    "Pattern: a prompt-chain on the Claude Agent SDK — each tool exposed via an in-process MCP server.",
    "Context: your live menu, options and stock are loaded each turn and prompt-cached, so it only offers what's actually in — and it re-reads stock right before it writes.",
    "Tools: read-only catalogue/stock lookups, one idempotent order-writer (a retry can't create a duplicate order), and an optional payment-link generator.",
    "Guardrails: it can't write an order without a confirmed total, never auto-charges, routes low-confidence or custom orders to a human queue, and holds least-privilege access to your POS.",
    "Untrusted input: customer messages are treated as untrusted (prompt-injection resistant); names, phone numbers and addresses are handled PDPA/PDPC-aligned.",
    "Recovery: on a tool failure or low confidence it retries, then hands to your team — never a silent wrong order.",
    "Observability: every tool call and the final order are logged and traceable, so any order can be reconstructed.",
    "Eval harness: a golden set of real historical order chats — including messy 'rojak', mixed-language and edge cases — regression-tested on every change before it touches live orders.",
  ],
  modelChoice: {
    approach: "Single model",
    rationale:
      "Order-taking is a short, sequential conversation sharing one context, so a single well-instrumented model is the right call — fast, cheap per order, and easy to audit.",
    costNote:
      "We reserve multi-agent designs (≈15× the tokens) for genuinely parallel work — not for a single ordering conversation.",
  },
  models: [
    {
      task: "Understand the order & catch nuance",
      model: "Claude Sonnet",
      why: "Reliable understanding of messy, mixed-language chat and special requests.",
    },
    {
      task: "Match items to the menu / catalogue",
      model: "Claude Haiku",
      why: "Cheap and fast for the bulk of straightforward item matching.",
    },
    {
      task: "Resolve ambiguous or unusual orders",
      model: "Claude Sonnet",
      why: "Handles the tricky orders — substitutions, custom requests — that Haiku flags.",
    },
  ],
  cost: {
    rows: [
      {
        label: "AI usage — per order",
        value: "~RM 0.10–0.30",
        note: "a short ordering conversation at Claude token rates",
      },
      {
        label: "AI usage — at 1,000 orders/month",
        value: "~RM 85–260 / month",
        note: "scales with order volume and conversation length",
      },
      {
        label: "Build (one-off)",
        value: "scoped per integration",
        note: "connecting WhatsApp, your menu and your order system is a few weeks; we quote fixed after a short discovery",
      },
      {
        label: "Ongoing",
        value: "monitoring + support",
        note: "small next to the orders it saves after hours and the re-keying it removes",
      },
    ],
    disclaimer:
      "Indicative only, shown in Ringgit at roughly RM 4.09 to the USD (mid-market, 11 Aug 2026); per-token rates follow Anthropic's published pricing (confirm current figures). Actual AI usage depends on conversation length and monthly order volume. You can model the same figures in SGD or your currency with our Claude cost calculator.",
  },
  evaluation: [
    "Deterministic checks first — item exists, option chosen, total adds up",
    "Self-verification — the agent re-confirms the itemised order before creating it",
    "Business KPIs — orders captured after hours, order accuracy, and staff time saved",
  ],
  evaluationDetail: {
    framework:
      "We score the agent with the Agent GPA framework — Goal, Plan, Action, a current standard for agent reliability — on top of the human baseline.",
    baseline:
      "First we measure your current WhatsApp ordering on a sample — time per order, how many come out wrong, and how many after-hours messages are missed. That human baseline is what every agent metric is measured against, so the lift is provable rather than assumed.",
    dimensions: [
      {
        dim: "Goal",
        desc: "Did it capture the right order?",
        metrics: [
          {
            name: "Order accuracy vs a human",
            measures: "The final order matches what the customer actually wanted, on held-out chats",
            target: "≥ 95%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "Was the conversation sound and complete?",
        metrics: [
          {
            name: "Required details captured",
            measures: "Items, options, quantity and delivery/pickup all collected — nothing missing",
            target: "100%",
          },
          {
            name: "No needless back-and-forth",
            measures: "It only asks when something is genuinely unclear",
            target: "monitored",
          },
        ],
      },
      {
        dim: "Action",
        desc: "Were the individual steps correct?",
        metrics: [
          {
            name: "Item match accuracy",
            measures: "Chat items mapped to the right menu/catalogue lines",
            target: "≥ 97%",
          },
          {
            name: "Total calculation",
            measures: "The order total is arithmetically correct every time",
            target: "100%",
          },
          {
            name: "No invented items",
            measures: "Never adds something not on your menu or not requested",
            target: "0 tolerated",
          },
        ],
      },
    ],
    gate: "The agent doesn't take a live order until it beats the human baseline and clears these GPA targets on a golden set of real historical order chats. Every change is regression-tested against the same set before it ships.",
  },
  delivery:
    "We start with a proof-of-concept on your real WhatsApp order chats, pilot it on one channel or outlet with staff oversight, then take it live. A focused single-outlet rollout is a matter of weeks.",
  ctaTitle: "Taking orders on WhatsApp by hand?",
  ctaDescription:
    "Tell us your menu size and order volume and we'll tell you honestly whether an order processing agent is worth building — and what it would take.",
  faqs: [
    {
      question: "Does it take payment?",
      answer:
        "It can send a payment link and confirm once paid, but it never charges a card on its own. You decide whether payment is captured in-chat or on fulfilment.",
    },
    {
      question: "Does it handle Malay, English and Chinese — and mixed messages?",
      answer:
        "Yes. It's built for how Malaysians actually type — Bahasa Malaysia, English, Chinese and 'rojak' mixes — and confirms the order clearly in the chat.",
    },
    {
      question: "What systems can it push orders into?",
      answer:
        "Your POS, an order sheet (e.g. Google Sheets), or your CRM — whatever your kitchen or floor already works from. We connect it during the build.",
    },
    {
      question: "What about custom orders or substitutions?",
      answer:
        "It clarifies with the customer where it can, and flags anything unusual for a human to confirm — nothing odd goes through silently.",
    },
  ],
};

const ms: UseCaseContent = {
  industry: "Runcit & F&B",
  title: "Ejen pemprosesan pesanan",
  cardSummary:
    "Terima pesanan pelanggan di WhatsApp — rakam item, sahkan butiran, dan hantar terus ke sistem anda.",
  heroSubhead:
    "Kami mereka bentuk, membina dan melaksanakan ejen AI WhatsApp yang menerima pesanan pelanggan dari mula hingga akhir — merakam item, mengesahkan pilihan dan jumlah, mengumpul butiran penghantaran atau ambil sendiri, dan memasukkan pesanan yang kemas ke sistem anda — supaya pasukan anda berhenti menaip semula dan berhenti terlepas mesej.",
  snapshot: {
    valueLabel: "Sangat tinggi",
    valueWhy:
      "WhatsApp ialah tempat kebanyakan PKS F&B & runcit di rantau ini sudah menerima pesanan — ini merakam hasil yang anda hilang selepas waktu dan waktu puncak.",
    complexityLabel: "Rendah–sederhana",
    complexityWhy:
      "Satu rantaian prompt linear atas model tunggal; kerja sebenar ialah menyambung WhatsApp, menu/stok dan POS anda — tanpa visi atau pematuhan berat.",
    timeToLive: "~2–4 minggu (satu cawangan)",
    bestFit: "PKS F&B & runcit yang menerima pesanan di WhatsApp",
  },
  outcomes: [
    "Rakam permintaan selepas waktu & waktu puncak yang anda hilang kepada pesaing hari ini",
    "Tiada lagi menaip semula daripada chat ke sistem anda",
    "Kurang pesanan salah — kurang pembaziran, kurang bayaran balik dan buat semula",
    "Kakitangan kembali ke lantai dan dapur, bukan terperangkap di telefon",
  ],
  impact: {
    manualLabel: "Uruskan pesanan secara manual",
    manualValue: "~4–6 min",
    agentLabel: "Dengan ejen",
    agentValue: "~30 saat semak",
    saved:
      "≈ 4–5 minit dijimatkan setiap pesanan — kira-kira 35–65 jam sebulan pada 500–800 pesanan. Dan setiap pesanan selepas waktu yang ditangkapnya ialah hasil yang anda hilang menjelang pagi: pada ~RM 0.20 kos AI, satu pesanan diselamatkan sehari sudah lebih daripada membayarnya.",
    note: "Anggaran; masa dan volum sebenar bergantung pada kerumitan pesanan dan variasi menu anda.",
  },
  businessProblem:
    "Perniagaan F&B atau runcit di rantau ini menerima aliran pesanan di WhatsApp. Kakitangan membaca setiap mesej, memikirkan apa yang pelanggan mahu, menjelaskan saiz dan tambahan, mengira jumlah, kemudian menaip semula ke POS atau hamparan — dan mengesahkan kepada pelanggan. Ia perlahan pada waktu puncak, mudah tersilap, dan mesej selepas waktu terbiar hingga pagi, apabila pelanggan sudah memesan di tempat lain.",
  manualWorkflow: {
    steps: [
      "Baca mesej WhatsApp yang masuk",
      "Fikirkan item, saiz dan tambahan",
      "Balas untuk menjelaskan yang tidak jelas",
      "Kira kuantiti dan jumlah",
      "Taip semula pesanan ke POS atau hamparan",
      "Sahkan kembali kepada pelanggan",
    ],
    breaks:
      "Halangan ialah menukar chat teks bebas kepada pesanan yang kemas dan betul — bahagian perlahan dan berulang yang tersekat pada waktu puncak dan berhenti terus selepas waktu.",
  },
  agenticWorkflow: {
    narrative:
      "Kami membinanya sebagai aliran prompt-chaining: setiap mesej pelanggan melalui rantaian pendek yang boleh dipercayai — faham → padan dengan menu → sahkan → rakam butiran → cipta pesanan — dengan ejen bertanya semula hanya apabila sesuatu benar-benar samar atau kehabisan stok.",
    steps: [
      "Fahami pesanan daripada chat",
      "Padan item dengan menu / katalog anda",
      "Sahkan item, pilihan & jumlah",
      "Rakam butiran penghantaran atau ambil sendiri",
      "Cipta pesanan dalam sistem anda",
    ],
    inLabel: "Pelanggan menghantar mesej di WhatsApp",
    outLabel: "Pesanan kemas dalam sistem anda",
    loopAround: {
      index: 1,
      label: "Bertanya semula hanya apabila item samar atau kehabisan stok — jika tidak, ia terus bergerak.",
    },
    humanStep: "Pasukan anda dapat pesanan kemas untuk dipenuhi",
  },
  workedExample: {
    inLabel: "Mesej pelanggan",
    inMessage:
      "nak order 2 nasi lemak ayam, 1 jgn pedas, teh ais kurang manis 1, pickup around 1pm",
    outLabel: "Pesanan kemas dicipta",
    outLines: [
      "2× Nasi Lemak Ayam — 1 tanpa sambal",
      "1× Teh Ais — kurang manis",
      "Ambil sendiri · 1:00 PM hari ini",
    ],
    outResult: "Jumlah: RM 23.50 — disahkan",
    note: "Dirakam daripada satu mesej, tanpa kakitangan menaip · ~RM 0.10–0.30 kos AI · ditulis terus ke POS anda.",
  },
  workflowDetail: [
    {
      title: "Fahami pesanan",
      detail:
        "Ejen membaca mesej pelanggan — dalam Bahasa Malaysia, Inggeris, Cina atau campuran yang orang taip — dan memikirkan apa yang mereka mahu pesan, termasuk kuantiti dan permintaan khas.",
    },
    {
      title: "Padan dengan menu",
      detail:
        "Ia memetakan apa yang pelanggan kata kepada menu atau katalog sebenar anda, menyelesaikan gelaran dan singkatan, dan mengesan yang kehabisan stok atau perlu saiz/pilihan dipilih.",
    },
    {
      title: "Sahkan item, pilihan dan jumlah",
      detail:
        "Ia mengesahkan pesanan terperinci dan jumlah kepada pelanggan dalam chat, dan hanya bertanya apabila sesuatu benar-benar tidak jelas — bukan setiap baris.",
    },
    {
      title: "Rakam butiran penghantaran atau ambil sendiri",
      detail:
        "Ia mengumpul butiran yang anda perlukan — ambil sendiri atau hantar, alamat, masa, dan (jika mahu) menghantar pautan bayaran — tanpa kakitangan menaip apa-apa.",
    },
    {
      title: "Cipta pesanan dalam sistem anda",
      detail:
        "Ia menulis pesanan berstruktur dan terperinci ke POS, helaian pesanan atau CRM anda, sedia untuk dapur atau lantai — tiada taip semula.",
    },
    {
      title: "Serah kepada pasukan anda",
      detail:
        "Pasukan anda dapat pesanan kemas untuk dipenuhi, dan apa-apa yang luar biasa ditandakan untuk manusia lihat.",
    },
  ],
  howWeBuild:
    "Kerana ia bekerja daripada menu dan stok langsung anda, ejen hanya menawarkan apa yang benar-benar tersedia — dan kerana ia menulis pesanan melalui alat yang disambung, bukan menaip bebas, tiada yang tersilap taip. Ia tidak pernah mengecaj kad sendiri, dan setiap pesanan yang dicipta dilog untuk anda audit.",
  agentLoop: [
    "chat + menu & stok langsung anda",
    "padan katalog & tulis pesanan (alat MCP)",
    "semak semula setiap item dan jumlah",
  ],
  integrations: [
    "WhatsApp Business API",
    "Menu atau katalog produk anda (+ stok)",
    "POS, helaian pesanan atau CRM anda",
    "Pilihan: pautan bayaran",
  ],
  architecture: [
    "Corak: rantaian prompt atas Claude Agent SDK — setiap alat didedahkan melalui pelayan MCP dalam-proses.",
    "Konteks: menu, pilihan dan stok langsung anda dimuatkan setiap giliran dan di-cache-prompt, jadi ia hanya menawarkan apa yang benar-benar ada — dan membaca semula stok tepat sebelum menulis.",
    "Alat: semakan katalog/stok baca-sahaja, satu penulis pesanan idempoten (ulangan tidak boleh mencipta pesanan pendua), dan (pilihan) penjana pautan bayaran.",
    "Pagar keselamatan: ia tidak boleh menulis pesanan tanpa jumlah yang disahkan, tidak pernah mengecaj automatik, menghalakan pesanan keyakinan-rendah atau tersuai ke baris gilir manusia, dan memegang akses keistimewaan-minimum ke POS anda.",
    "Input tidak dipercayai: mesej pelanggan dianggap tidak dipercayai (tahan suntikan-prompt); nama, nombor telefon dan alamat dikendalikan selaras PDPA/PDPC.",
    "Pemulihan: apabila alat gagal atau keyakinan rendah ia mencuba semula, kemudian menyerahkan kepada pasukan anda — tiada pesanan salah secara senyap.",
    "Kebolehlihatan: setiap panggilan alat dan pesanan akhir dilog dan boleh dijejak, jadi mana-mana pesanan boleh dibina semula.",
    "Rangka penilaian: set emas chat pesanan sejarah sebenar — termasuk 'rojak', berbilang bahasa dan kes tepi — diuji regresi pada setiap perubahan sebelum menyentuh pesanan langsung.",
  ],
  modelChoice: {
    approach: "Model tunggal",
    rationale:
      "Menerima pesanan ialah perbualan pendek dan berurutan yang berkongsi satu konteks, jadi model tunggal yang lengkap adalah pilihan tepat — pantas, murah setiap pesanan, dan mudah diaudit.",
    costNote:
      "Kami menyimpan reka bentuk berbilang-ejen (≈15× token) untuk kerja yang benar-benar selari — bukan untuk satu perbualan pesanan.",
  },
  models: [
    {
      task: "Fahami pesanan & tangkap nuansa",
      model: "Claude Sonnet",
      why: "Pemahaman yang boleh dipercayai untuk chat berbilang bahasa yang berselerak dan permintaan khas.",
    },
    {
      task: "Padan item dengan menu / katalog",
      model: "Claude Haiku",
      why: "Murah dan pantas untuk sebahagian besar pemadanan item yang mudah.",
    },
    {
      task: "Selesaikan pesanan samar atau luar biasa",
      model: "Claude Sonnet",
      why: "Mengendalikan pesanan rumit — penggantian, permintaan tersuai — yang ditandakan Haiku.",
    },
  ],
  cost: {
    rows: [
      {
        label: "Penggunaan AI — setiap pesanan",
        value: "~RM 0.10–0.30",
        note: "perbualan pesanan pendek pada kadar token Claude",
      },
      {
        label: "Penggunaan AI — pada 1,000 pesanan/bulan",
        value: "~RM 85–260 / bulan",
        note: "berskala mengikut volum pesanan dan panjang perbualan",
      },
      {
        label: "Pembinaan (sekali)",
        value: "diskop mengikut integrasi",
        note: "menyambung WhatsApp, menu dan sistem pesanan anda adalah beberapa minggu; sebut harga tetap selepas penemuan ringkas",
      },
      {
        label: "Berterusan",
        value: "pemantauan + sokongan",
        note: "kecil berbanding pesanan yang diselamatkan selepas waktu dan taip semula yang dihapuskan",
      },
    ],
    disclaimer:
      "Anggaran sahaja, ditunjukkan dalam Ringgit pada kira-kira RM 4.09 kepada USD (kadar pasaran, 11 Ogos 2026); kadar setiap token mengikut harga yang diterbitkan Anthropic (sahkan angka semasa). Penggunaan AI sebenar bergantung pada panjang perbualan dan volum pesanan bulanan. Anda boleh anggarkan angka yang sama dalam SGD atau mata wang anda dengan kalkulator kos Claude kami.",
  },
  evaluation: [
    "Semakan deterministik dahulu — item wujud, pilihan dipilih, jumlah betul",
    "Pengesahan sendiri — ejen mengesahkan semula pesanan terperinci sebelum menciptanya",
    "KPI perniagaan — pesanan dirakam selepas waktu, ketepatan pesanan, dan masa kakitangan dijimatkan",
  ],
  evaluationDetail: {
    framework:
      "Kami menilai ejen dengan rangka kerja Agent GPA — Goal, Plan, Action, satu standard semasa untuk kebolehpercayaan ejen — di atas garis dasar manusia.",
    baseline:
      "Mula-mula kami mengukur proses pesanan WhatsApp semasa anda pada sampel — masa setiap pesanan, berapa yang tersilap, dan berapa mesej selepas waktu terlepas. Garis dasar manusia itu menjadi ukuran untuk setiap metrik ejen, jadi peningkatan boleh dibuktikan.",
    dimensions: [
      {
        dim: "Goal",
        desc: "Adakah ia merakam pesanan yang betul?",
        metrics: [
          {
            name: "Ketepatan pesanan vs manusia",
            measures: "Pesanan akhir sepadan dengan yang pelanggan mahu, pada chat terpencil",
            target: "≥ 95%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "Adakah perbualan kukuh dan lengkap?",
        metrics: [
          {
            name: "Butiran diperlukan dirakam",
            measures: "Item, pilihan, kuantiti dan penghantaran/ambil sendiri semua dikumpul",
            target: "100%",
          },
          {
            name: "Tiada soal-jawab berlebihan",
            measures: "Ia hanya bertanya apabila sesuatu benar-benar tidak jelas",
            target: "dipantau",
          },
        ],
      },
      {
        dim: "Action",
        desc: "Adakah setiap langkah betul?",
        metrics: [
          {
            name: "Ketepatan padanan item",
            measures: "Item chat dipetakan ke baris menu/katalog yang betul",
            target: "≥ 97%",
          },
          {
            name: "Pengiraan jumlah",
            measures: "Jumlah pesanan betul dari segi aritmetik setiap kali",
            target: "100%",
          },
          {
            name: "Tiada item direka",
            measures: "Tidak pernah menambah yang tiada dalam menu atau tidak diminta",
            target: "0 dibenarkan",
          },
        ],
      },
    ],
    gate: "Ejen tidak menerima pesanan langsung sehingga ia mengatasi garis dasar manusia dan mencapai sasaran GPA ini pada set emas chat pesanan sejarah sebenar. Setiap perubahan diuji regresi sebelum dihantar.",
  },
  delivery:
    "Kami bermula dengan bukti-konsep atas chat pesanan WhatsApp sebenar anda, menguji pada satu saluran atau cawangan dengan pengawasan kakitangan, kemudian melancarkannya. Pelancaran satu cawangan yang fokus mengambil masa beberapa minggu.",
  ctaTitle: "Menerima pesanan WhatsApp secara manual?",
  ctaDescription:
    "Beritahu kami saiz menu dan volum pesanan anda, dan kami akan beritahu dengan jujur sama ada ejen pemprosesan pesanan berbaloi dibina — dan apa yang diperlukan.",
  faqs: [
    {
      question: "Adakah ia menerima bayaran?",
      answer:
        "Ia boleh menghantar pautan bayaran dan mengesahkan setelah dibayar, tetapi ia tidak pernah mengecaj kad sendiri. Anda tentukan sama ada bayaran diambil dalam chat atau semasa pemenuhan.",
    },
    {
      question: "Adakah ia mengendalikan Melayu, Inggeris dan Cina — dan mesej campuran?",
      answer:
        "Ya. Ia dibina untuk cara rakyat Malaysia menaip — Bahasa Malaysia, Inggeris, Cina dan campuran 'rojak' — dan mengesahkan pesanan dengan jelas dalam chat.",
    },
    {
      question: "Sistem apa yang boleh ia masukkan pesanan?",
      answer:
        "POS anda, helaian pesanan (cth. Google Sheets), atau CRM anda — apa sahaja yang dapur atau lantai anda sudah guna. Kami menyambungnya semasa pembinaan.",
    },
    {
      question: "Bagaimana dengan pesanan tersuai atau penggantian?",
      answer:
        "Ia menjelaskan dengan pelanggan di mana boleh, dan menandakan apa-apa yang luar biasa untuk manusia sahkan — tiada yang pelik lolos secara senyap.",
    },
  ],
};

const zh: UseCaseContent = {
  industry: "零售与餐饮",
  title: "订单处理智能体",
  cardSummary: "在 WhatsApp 上接收客户订单——记录商品、确认细节，并直接写入你的系统。",
  heroSubhead:
    "我们设计、构建并部署一个 WhatsApp AI 智能体，端到端地接收客户订单——记录商品、确认选项与总额、收集配送或自取信息，并把一份干净的订单写入你的系统——让你的团队不必再重复录入、也不再漏掉消息。",
  snapshot: {
    valueLabel: "非常高",
    valueWhy:
      "WhatsApp 正是多数区域内的餐饮与零售中小企业接单的地方——这能接住你在营业时间外与高峰期流失的营收。",
    complexityLabel: "偏低",
    complexityWhy:
      "单一模型上的一条线性提示链；真正的工作是打通 WhatsApp、你的菜单/库存与 POS——无需视觉识别或繁重合规。",
    timeToLive: "约 2–4 周（单一门店）",
    bestFit: "在 WhatsApp 上接单的餐饮与零售中小企业",
  },
  outcomes: [
    "接住你如今正流失给对手的营业时间外与高峰需求",
    "不必再把聊天内容手动录入系统",
    "更少错单——更少浪费、更少退款与返工",
    "员工回到店面和厨房，而不是困在电话上",
  ],
  impact: {
    manualLabel: "人工处理一单",
    manualValue: "约 4–6 分钟",
    agentLabel: "使用智能体",
    agentValue: "约 30 秒复核",
    saved:
      "每单约节省 4–5 分钟——每月 500–800 单约合 35–65 小时。而它接住的每一笔营业时间外订单，都是你原本到早上就流失的营收：AI 成本约 RM 0.20，每天挽回一单便已远超其成本。",
    note: "为估算；实际时间与单量取决于订单复杂度与菜单变化程度。",
  },
  businessProblem:
    "区域内的餐饮或零售商家在 WhatsApp 上接到一连串订单。员工阅读每条消息、弄清客户想要什么、确认规格与加料、算出总额，再录入 POS 或表格——并回复确认客户。午市高峰时很慢、易出错，营业时间外的消息一直堆到早上，那时客户早已在别处下单。",
  manualWorkflow: {
    steps: [
      "阅读收到的 WhatsApp 消息",
      "弄清商品、规格与加料",
      "回复以澄清不清楚之处",
      "统计数量与总额",
      "把订单录入 POS 或表格",
      "回复确认客户",
    ],
    breaks: "瓶颈在于把自由文本聊天变成一份干净、正确的订单——这个缓慢重复的环节在高峰期堵塞、在营业时间外完全停摆。",
  },
  agenticWorkflow: {
    narrative:
      "我们以 prompt-chaining（提示链）方式构建：每条客户消息都经过一条简短可靠的链路——理解 → 匹配菜单 → 确认 → 记录细节 → 生成订单——只有当某项确实含糊或缺货时智能体才会追问。",
    steps: [
      "从聊天中理解订单",
      "将商品匹配到你的菜单/目录",
      "确认商品、选项与总额",
      "记录配送或自取信息",
      "在你的系统中生成订单",
    ],
    inLabel: "客户在 WhatsApp 上发来消息",
    outLabel: "系统里一份干净的订单",
    loopAround: {
      index: 1,
      label: "仅当某项商品含糊或缺货时才向客户追问——否则持续推进。",
    },
    humanStep: "你的团队拿到一份干净的订单去履行",
  },
  workedExample: {
    inLabel: "客户消息",
    inMessage:
      "要 2 份 nasi lemak ayam，一份不要辣，teh ais 少甜 1，大概 1pm 来拿",
    outLabel: "已生成干净订单",
    outLines: [
      "2× Nasi Lemak Ayam — 1 份不辣",
      "1× Teh Ais — 少甜",
      "自取 · 今天 1:00 PM",
    ],
    outResult: "合计：RM 23.50 — 已确认",
    note: "从一条消息中捕获，无需员工输入 · AI 成本约 RM 0.10–0.30 · 直接写入你的 POS。",
  },
  workflowDetail: [
    {
      title: "理解订单",
      detail:
        "智能体阅读客户消息——马来语、英语、中文或人们实际混用的语言——弄清他们想下的单，包括数量和特殊要求。",
    },
    {
      title: "匹配你的菜单",
      detail:
        "它把客户所说的映射到你真实的菜单或目录，处理别称和简写，并识别缺货或需要选择规格/选项的项目。",
    },
    {
      title: "确认商品、选项与总额",
      detail:
        "它在聊天中向客户确认逐项订单与总额，只有当确实不清楚时才提问——而不是逐行都问。",
    },
    {
      title: "记录配送或自取信息",
      detail:
        "它收集你需要的信息——自取或配送、地址、时间，并（如你希望）发送支付链接——无需员工输入任何内容。",
    },
    {
      title: "在你的系统中生成订单",
      detail:
        "它把一份结构化、逐项的订单写入你的 POS、订单表或 CRM，供厨房或店面直接使用——无需重复录入。",
    },
    {
      title: "交给你的团队",
      detail:
        "你的团队拿到一份干净的订单去履行，任何异常都会标记出来供人快速查看。",
    },
  ],
  howWeBuild:
    "因为它基于你的实时菜单与库存工作，智能体只会提供真正有货的商品——又因为它通过连接的工具写入订单、而非手动输入，不会打错字。它绝不自行扣款，它生成的每一单都会留痕供你审计。",
  agentLoop: [
    "聊天 + 你的实时菜单与库存",
    "匹配目录并写入订单（MCP 工具）",
    "复核每项商品与总额",
  ],
  integrations: [
    "WhatsApp Business API",
    "你的菜单或产品目录（含库存）",
    "你的 POS、订单表或 CRM",
    "可选：支付链接",
  ],
  architecture: [
    "模式：基于 Claude Agent SDK 的提示链——每个工具通过进程内 MCP 服务器提供。",
    "上下文：你的实时菜单、选项与库存每轮都会载入并做提示缓存，因此它只提供真正有货的商品——并在写入前重新读取库存。",
    "工具：只读的目录/库存查询、一个幂等的订单写入器（重试不会生成重复订单），以及（可选）支付链接生成器。",
    "防护机制：没有确认总额它就无法写入订单，绝不自动扣款，把低置信度或定制订单转入人工队列，并对你的 POS 采用最小权限访问。",
    "不可信输入：客户消息被视为不可信（可抵御提示注入）；姓名、电话与地址均按 PDPA/PDPC 处理。",
    "恢复：工具失败或置信度低时它会重试，然后交给你的团队——绝不静默生成错单。",
    "可观测性：每次工具调用与最终订单都会留痕且可追溯，任何订单都能被还原。",
    "评估框架：一组真实历史订单聊天的黄金集——含“rojak”、混合语言与边缘案例——每次改动在接触实时订单前都做回归测试。",
  ],
  modelChoice: {
    approach: "单一模型",
    rationale:
      "接单是一段简短、顺序进行、共享同一上下文的对话，因此一个完善配置的单一模型是正确选择——快速、每单成本低、也易审计。",
    costNote:
      "我们把多智能体设计（约 15 倍 token）留给真正并行的工作——而非一段接单对话。",
  },
  models: [
    {
      task: "理解订单并捕捉细微差别",
      model: "Claude Sonnet",
      why: "对杂乱、混合语言的聊天与特殊要求具备可靠的理解力。",
    },
    {
      task: "将商品匹配到菜单/目录",
      model: "Claude Haiku",
      why: "对绝大多数直接的商品匹配最便宜、最快。",
    },
    {
      task: "处理含糊或异常订单",
      model: "Claude Sonnet",
      why: "处理棘手订单——替换、定制要求——即 Haiku 标记出来的部分。",
    },
  ],
  cost: {
    rows: [
      {
        label: "AI 用量——每单",
        value: "约 RM 0.10–0.30",
        note: "一段简短的接单对话，按 Claude 的 token 费率",
      },
      {
        label: "AI 用量——每月 1,000 单",
        value: "约 RM 85–260 / 月",
        note: "随订单量与对话长度而变",
      },
      {
        label: "构建（一次性）",
        value: "按集成范围报价",
        note: "接入 WhatsApp、你的菜单与订单系统约需数周；简短的需求梳理后给出固定报价",
      },
      {
        label: "持续运营",
        value: "监控 + 支持",
        note: "相比其在营业时间外挽回的订单与省去的重复录入，成本很小",
      },
    ],
    disclaimer:
      "仅为估算，按约 1 美元兑 4.09 令吉（市场中间价，2026 年 8 月 11 日）以令吉显示；每 token 费率以 Anthropic 公布的定价为准（请核对最新数字）。实际 AI 用量取决于对话长度与每月订单量。你可以用我们的 Claude 成本计算器把同样的数字换算成 SGD 或你的货币。",
  },
  evaluation: [
    "先做确定性检查——商品存在、已选选项、总额正确",
    "自我验证——智能体在生成订单前再次确认逐项内容",
    "业务 KPI——营业时间外接到的订单、订单准确率、以及节省的员工时间",
  ],
  evaluationDetail: {
    framework:
      "我们采用 Agent GPA 框架——Goal、Plan、Action，当前衡量智能体可靠性的标准——并结合人工基线来评估。",
    baseline:
      "我们先在样本上测量你当前的 WhatsApp 接单——每单耗时、有多少出错、有多少营业时间外的消息被漏掉。这个人工基线是衡量每个智能体指标的基准，因此提升是可证明的。",
    dimensions: [
      {
        dim: "Goal",
        desc: "是否记录了正确的订单？",
        metrics: [
          {
            name: "订单准确率（对比人工）",
            measures: "在留出聊天上，最终订单与客户真正想要的一致",
            target: "≥ 95%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "对话是否合理且完整？",
        metrics: [
          {
            name: "必需信息已收集",
            measures: "商品、选项、数量与配送/自取全部收集——无遗漏",
            target: "100%",
          },
          {
            name: "无多余往返",
            measures: "只有当某项确实不清楚时才提问",
            target: "持续监控",
          },
        ],
      },
      {
        dim: "Action",
        desc: "各个步骤是否正确？",
        metrics: [
          {
            name: "商品匹配准确率",
            measures: "聊天中的商品映射到正确的菜单/目录条目",
            target: "≥ 97%",
          },
          {
            name: "总额计算",
            measures: "订单总额每次在算术上都正确",
            target: "100%",
          },
          {
            name: "不凭空添加商品",
            measures: "绝不添加菜单上没有或客户未要求的东西",
            target: "零容忍",
          },
        ],
      },
    ],
    gate: "在智能体于真实历史订单聊天黄金集上超过人工基线并达到这些 GPA 目标之前，它不会接收任何实时订单。每次改动在上线前都要针对同一数据集做回归测试。",
  },
  delivery:
    "我们先在你真实的 WhatsApp 订单聊天上做概念验证，在一个渠道或门店以员工监督试运行，然后正式上线。聚焦单一门店的落地只需数周。",
  ctaTitle: "还在用人工处理 WhatsApp 订单？",
  ctaDescription:
    "告诉我们你的菜单规模与订单量，我们会诚实告诉你订单处理智能体是否值得构建，以及需要什么。",
  faqs: [
    {
      question: "它会收款吗？",
      answer:
        "它可以发送支付链接并在付款后确认，但绝不会自行扣款。由你决定是在聊天中收款还是在履行时收款。",
    },
    {
      question: "它能处理马来语、英语和中文——以及混合消息吗？",
      answer:
        "可以。它按马来西亚人实际的输入方式构建——马来语、英语、中文及“rojak”混合——并在聊天中清晰确认订单。",
    },
    {
      question: "它能把订单写入哪些系统？",
      answer:
        "你的 POS、订单表（如 Google Sheets）或 CRM——你的厨房或店面已经在用的任何系统。我们在构建时对接。",
    },
    {
      question: "定制订单或替换怎么办？",
      answer:
        "它会尽量向客户澄清，并把任何异常标记出来供人确认——不会有奇怪的内容悄悄通过。",
    },
  ],
};

export const orderProcessing: AgentUseCase = {
  structure: {
    slug: "order-processing",
    status: "available",
    pattern: "prompt-chaining",
    route: "solutions/agent-use-cases/order-processing",
    heroImage: "/images/agent-use-cases/order-processing-hero.jpg",
    cardImage: "/images/agent-use-cases/order-processing-card.jpg",
    relatedUseCases: ["product-enquiry", "support-triage"],
    trackLocation: "agent_use_case_order_processing",
    valueScore: 5,
    complexityScore: 2,
  },
  content: { en, ms, zh },
};
